import { useState, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Message, MessageContextType, MessageMetadata } from '@/types/insurance';
import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';
import { extractMailTemplate, extractEmailTitle } from '@/lib/emailUtils';
import { detectLanguage } from '@/lib/languageUtils';
import { useChatContextData } from './useChatContextData';
import { logError } from '@/lib/errorLogger';
import { useAuth } from '@/context/AuthContext';
import { messageContainsEmailOrTemplate } from '@/lib/utils';

interface UseChatMessagesResult {
  messages: Message[];
  addMessage: (content: string, role: 'user' | 'assistant' | 'system', contextType?: MessageContextType, metadata?: MessageMetadata) => Promise<Message | null>;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  loadMessages?: () => Promise<void>;
  isProcessingMessage: React.MutableRefObject<boolean>;
  streamingMessageId: React.MutableRefObject<string | null>;
  processedMessageIds: React.MutableRefObject<Set<string>>;
  setIsStreaming: (streaming: boolean) => void;
  setCurrentStreamedContent: (content: string) => void;
}

interface UseChatContextDataResult {
  context: any;
  updateContext: (newContext: any) => Promise<void>;
  loadContext: () => Promise<void>;
  useOpenAI: boolean;
  toggleModel: () => void;
  threadId?: string;
}

interface UseChatMessageProcessingProps {
  chatId?: string;
  messagesData: UseChatMessagesResult;
  contextData: UseChatContextDataResult;
}

export const useChatMessageProcessing = ({ chatId, messagesData, contextData }: UseChatMessageProcessingProps) => {
  const { user } = useAuth();
  const { messages, addMessage, setIsStreaming, setCurrentStreamedContent } = messagesData;
  const { useOpenAI, threadId } = contextData;
  
  const isGeneratingTemplateRef = useRef(false);
  const templateRequestInProgressRef = useRef(false);
  const templateGenerationAttempts = useRef(0);
  const accumulatedHtmlRef = useRef<string>('');
  
  const processMessageWithOpenAI = async (
    content: string,
    contextType?: MessageContextType,
    metadata?: MessageMetadata
  ) => {
    if (!chatId || !user) {
      console.error('Missing required data for OpenAI processing');
      return null;
    }
    
    if (messagesData.isProcessingMessage.current) {
      console.log('Already processing a message, skipping duplicate request');
      return null;
    }
    
    const isTemplateGeneration = metadata?.emailStage === 'template' && 
                                metadata?.isGeneratingTemplate;
    
    if (isTemplateGeneration) {
      if (isGeneratingTemplateRef.current) {
        console.log('Already generating a template, skipping duplicate request');
        return null;
      }
      
      isGeneratingTemplateRef.current = true;
      templateGenerationAttempts.current += 1;
      accumulatedHtmlRef.current = '';
      console.log(`Starting template generation attempt ${templateGenerationAttempts.current}`);
      
      if (templateGenerationAttempts.current > 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    try {
      messagesData.isProcessingMessage.current = true;
      setIsStreaming(true);
      
      console.log("Processing message with OpenAI, content length:", content.length, 
                 "contextType:", contextType,
                 "emailStage:", metadata?.emailStage);
      
      const userMessage = await addMessage(content, 'user', contextType, metadata);
      if (!userMessage) throw new Error('Failed to add user message');
      
      console.log("User message added with ID:", userMessage.id);
      
      const companyInfo = localStorage.getItem('companyInfo') 
        ? JSON.parse(localStorage.getItem('companyInfo')!) 
        : null;
      
      const detectedLanguage = metadata?.language || detectLanguage(content) || 'de';
      
      const format = metadata?.format || 'html';
      const emailStage = metadata?.emailStage || 'concept';
      
      const recentMessages = messages.slice(-10).map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      
      const placeholderId = crypto.randomUUID();
      messagesData.streamingMessageId.current = placeholderId;
      
      const placeholderMessage: Message = {
        id: placeholderId,
        chatId: chatId,
        role: 'assistant',
        content: '',
        createdAt: new Date().toISOString(),
        contextType,
        metadata: {
          ...metadata,
          emailStage,
          format,
          language: detectedLanguage
        },
        isStreaming: true
      };
      
      messagesData.setMessages(prev => [...prev, placeholderMessage]);
      setCurrentStreamedContent('');
      accumulatedHtmlRef.current = '';
      
      console.log("Added placeholder message with ID:", placeholderId);
      
      const SUPABASE_PROJECT_ID = 'qmuqbkpcfslysjbptlnj';
      const functionUrl = `https://${SUPABASE_PROJECT_ID}.supabase.co/functions/v1/openai-chat`;
      
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      
      if (!accessToken) {
        throw new Error('No access token found, user may not be authenticated');
      }
      
      console.log("Making API request to OpenAI chat function with emailStage:", emailStage, "format:", format);
      
      let processedContent = content;
      if (isTemplateGeneration) {
        processedContent = `
        You're responsible for generating a production-ready HTML email template based on an approved concept. 
        Please create a beautiful, responsive HTML email template that follows best practices.
        
        Original Concept:
        ${content}
        
        Use the brand colors if available: ${companyInfo?.brandColors?.primary || '#00796b'} and ${companyInfo?.brandColors?.secondary || '#4caf50'}
        
        Return valid, responsive, well-structured HTML that works in all email clients.
        `;
      }
      
      templateRequestInProgressRef.current = true;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          action: 'chat',
          message: processedContent,
          history: recentMessages,
          companyInfo,
          contextType,
          language: detectedLanguage,
          format,
          emailStage,
          streaming: true,
          isGeneratingTemplate: metadata?.isGeneratingTemplate || false,
          originalConcept: isTemplateGeneration ? content : undefined
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error Response:", response.status, errorText);
        throw new Error(`Stream response not ok: ${response.status} ${errorText}`);
      }
      
      console.log("API response received, status:", response.status);
      
      if (!response.body) {
        throw new Error('Response body is null');
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let finalContent = '';
      
      console.log('Starting to process streamed data');
      
      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          console.log('Stream completed, final content length:', finalContent.length);
          break;
        }
        
        const chunk = decoder.decode(value, { stream: true });
        console.log('Received chunk, length:', chunk.length);
        
        const lines = chunk.split('\n').filter(line => line.trim());
        
        for (const line of lines) {
          try {
            if (line.startsWith('data: ')) {
              const jsonString = line.substring(6);
              const data = JSON.parse(jsonString);
              
              if (data.type === 'content') {
                finalContent += data.content;
                setCurrentStreamedContent(finalContent);
                
                if (data.isTemplate && isTemplateGeneration) {
                  accumulatedHtmlRef.current = data.content;
                  console.log('Updated template HTML content, length:', accumulatedHtmlRef.current.length);
                  
                  // Dispatch content update event to ensure other components are aware
                  window.dispatchEvent(new CustomEvent('template-content-update', {
                    detail: { content: data.content }
                  }));
                }
                
                console.log('Updated streamed content, current length:', finalContent.length);
                
                messagesData.setMessages(prev => 
                  prev.map(msg => 
                    msg.id === placeholderId
                      ? { ...msg, content: finalContent }
                      : msg
                  )
                );
              } else if (data.type === 'template_update' && data.content) {
                // Special handling for template updates
                accumulatedHtmlRef.current = data.content;
                console.log('Received template update event, content length:', data.content.length);
                
                window.dispatchEvent(new CustomEvent('template-content-update', {
                  detail: { content: data.content }
                }));
              } else if (data.type === 'error') {
                console.error('Stream error:', data.error);
                throw new Error(data.error);
              } else if (data.type === 'done') {
                console.log('Stream completed via done message');
                if (data.isTemplate && data.content) {
                  accumulatedHtmlRef.current = data.content;
                  console.log('Final template content received, length:', data.content.length);
                }
                break;
              }
            }
          } catch (parseError) {
            console.error('Error parsing stream data:', parseError, 'Raw line:', line);
          }
        }
      }
      
      messagesData.setMessages(prev => prev.filter(msg => msg.id !== placeholderId));
      messagesData.streamingMessageId.current = null;
      
      await new Promise(resolve => setTimeout(resolve, 50));
      templateRequestInProgressRef.current = false;
      
      // Use accumulated HTML if we have it and we're generating a template
      if (isTemplateGeneration && accumulatedHtmlRef.current) {
        finalContent = accumulatedHtmlRef.current;
        console.log('Using accumulated HTML for final content, length:', finalContent.length);
      }
      
      if (finalContent.length === 0) {
        console.error('Final content is empty after streaming');
        throw new Error('Empty response received from streaming');
      }
      
      console.log('Final content ready to save to DB, length:', finalContent.length);
      
      const isEmail = messageContainsEmailOrTemplate(finalContent);
      let templateId: string | undefined = undefined;
      
      if (isEmail) {
        try {
          const { data: template, error: templateError } = await supabase
            .from('email_templates')
            .insert({
              user_id: user.id,
              content: finalContent,
              format: 'html',
              email_type: 'general',
              title: extractEmailTitle(finalContent)
            })
            .select('id')
            .single();
          
          if (!templateError && template) {
            templateId = template.id;
            console.log('Saved email template with ID:', templateId);
          }
        } catch (templateError) {
          console.error('Error saving email template:', templateError);
        }
      }
      
      const finalMessageId = crypto.randomUUID();
      
      console.log('Saving final message to DB with ID:', finalMessageId);
      
      const { data: assistantMessage, error: assistantError } = await supabase
        .from('messages')
        .insert({
          id: finalMessageId,
          chat_id: chatId,
          role: 'assistant',
          content: finalContent,
          context_type: contextType,
          metadata: JSON.stringify({
            ...metadata,
            language: detectedLanguage,
            emailStage,
            format
          }),
          is_email: isEmail,
          template_id: templateId
        })
        .select('*')
        .single();
        
      if (assistantError) {
        console.error('Error saving assistant message:', assistantError);
        throw assistantError;
      }
      
      console.log('Assistant message saved successfully:', assistantMessage.id);
      
      messagesData.processedMessageIds.current.add(finalMessageId);
      
      const finalMessage: Message = {
        id: assistantMessage.id,
        chatId: assistantMessage.chat_id,
        role: assistantMessage.role as 'assistant',
        content: assistantMessage.content,
        createdAt: assistantMessage.created_at,
        contextType: assistantMessage.context_type as MessageContextType | undefined,
        metadata: assistantMessage.metadata as MessageMetadata | undefined,
        referenceId: assistantMessage.reference_id,
        isEmail: assistantMessage.is_email || false,
        templateId: assistantMessage.template_id
      };
      
      console.log('Adding final message to UI state');
      messagesData.setMessages(prev => [...prev, finalMessage]);
      
      if (isTemplateGeneration && messageContainsEmailOrTemplate(finalContent)) {
        console.log("Template generation completed successfully");
        window.dispatchEvent(new CustomEvent('template-generation-complete', {
          detail: { content: finalContent }
        }));
      } else if (isTemplateGeneration) {
        window.dispatchEvent(new CustomEvent('template-generation-failed', {
          detail: { error: "Template generation finished but no valid HTML content!" }
        }));
      }
      
      return finalMessage;
    } catch (error) {
      console.error('Error in OpenAI processing:', error);
      logError(error as Error, 'useChatMessageProcessing');
      
      let errorMessage = 'Es tut mir leid, bei der Verarbeitung Ihrer Nachricht ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.';
      
      if (metadata?.language === 'fr') {
        errorMessage = 'Je suis désolé, une erreur s\'est produite lors du traitement de votre message. Veuillez réessayer.';
      } else if (metadata?.language === 'it') {
        errorMessage = 'Mi dispiace, si è verificato un errore durante l\'elaborazione del tuo messaggio. Per favore riprova.';
      } else if (isTemplateGeneration) {
        errorMessage = 'Es tut mir leid, aber die Template-Generierung ist fehlgeschlagen. Bitte versuchen Sie es erneut.';
        
        window.dispatchEvent(new CustomEvent('template-generation-failed', {
          detail: { error: (error as Error).message }
        }));
      }
      
      if (messagesData.streamingMessageId.current) {
        messagesData.setMessages(prev => 
          prev.filter(msg => msg.id !== messagesData.streamingMessageId.current)
        );
      }
      
      await addMessage(errorMessage, 'assistant', contextType, metadata);
      
      return null;
    } finally {
      setIsStreaming(false);
      setCurrentStreamedContent('');
      messagesData.isProcessingMessage.current = false;
      
      if (isTemplateGeneration) {
        setTimeout(() => {
          isGeneratingTemplateRef.current = false;
        }, 500);
      }
    }
  };
  
  const processMessageWithClaude = async (
    content: string,
    contextType?: MessageContextType,
    metadata?: MessageMetadata
  ) => {
    if (!chatId || !user) return null;
    
    try {
      const userMessage = await addMessage(content, 'user', contextType, metadata);
      if (!userMessage) throw new Error('Failed to add user message');
      
      const companyInfo = localStorage.getItem('companyInfo') 
        ? JSON.parse(localStorage.getItem('companyInfo')!) 
        : null;
      
      console.log('Sending request to chat-service with Claude');
      
      const detectedLanguage = metadata?.language || detectLanguage(content) || 'de';
      
      const { data, error } = await supabase.functions.invoke('chat-service', {
        body: {
          prompt: content,
          companyInfo,
          conversationHistory: messages.slice(-10),
          format: metadata?.format || 'html',
          language: detectedLanguage,
          chatId: chatId,
          service: 'chat'
        }
      });
      
      if (error) {
        console.error('Error from chat-service:', error);
        throw error;
      }
      
      if (!data || !data.content) {
        console.error('No content returned from chat-service');
        throw new Error('No content returned from API');
      }
      
      console.log('Received response from chat-service, content length:', data.content.length);
      
      const aiMessage = await addMessage(
        data.content, 
        'assistant', 
        contextType,
        {
          ...metadata,
          language: detectedLanguage
        }
      );
      
      return aiMessage;
    } catch (error) {
      console.error('Error in Claude processing:', error);
      
      let errorMessage = 'Es tut mir leid, bei der Verarbeitung Ihrer Nachricht ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.';
      
      if (metadata?.language === 'fr') {
        errorMessage = 'Je suis désolé, une erreur s\'est produite lors du traitement de votre message. Veuillez réessayer.';
      } else if (metadata?.language === 'it') {
        errorMessage = 'Mi dispiace, si è verificato un errore durante l\'elaborazione del tuo messaggio. Per favore riprova.';
      }
      
      await addMessage(errorMessage, 'assistant', contextType, metadata);
      
      return null;
    }
  };
  
  const processMessage = async (
    content: string,
    contextType?: MessageContextType,
    metadata?: MessageMetadata
  ) => {
    if (useOpenAI) {
      console.log('Processing message with OpenAI');
      return processMessageWithOpenAI(content, contextType, metadata);
    } else {
      console.log('Processing message with Claude');
      return processMessageWithClaude(content, contextType, metadata);
    }
  };
  
  return {
    processMessage,
    processMessageWithOpenAI,
    processMessageWithClaude,
    getCurrentTemplateHtml: () => accumulatedHtmlRef.current
  };
};
